// src/pages/admin/AdminPortal.jsx
// Full admin dashboard — stats, users, jobs, KYC + opportunity moderation with comments
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, getDocs, doc, updateDoc, limit, onSnapshot } from 'firebase/firestore';
import { addAdminComment, moderateOpportunity, toggleFeatured, toggleUrgent } from '../../services/opportunityService';
import { db } from '../../config/firebase';
import { useAuth } from '../../hooks/useAuth';
import { toast } from 'react-hot-toast';
import { C } from '../../styles/tokens';

// ── Mini Spinner ────────────────────────────────
const Spin = () => (
  <div style={{ width: 18, height: 18, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.2)', borderTopColor: '#fff', animation: 'spin .7s linear infinite' }} />
);

// ── Stat card ───────────────────────────────────
const StatCard = ({ icon, label, value, color, sub }) => (
  <div className="card" style={{ padding: 20 }}>
    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg,${color}70,transparent)` }} />
    <div style={{ width: 40, height: 40, borderRadius: 10, background: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 19, marginBottom: 12 }}>{icon}</div>
    <div className="syne" style={{ fontSize: 28, fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', fontWeight: 600, marginTop: 4 }}>{label}</div>
    {sub && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginTop: 2 }}>{sub}</div>}
  </div>
);

// ── Section header ──────────────────────────────
const SectionHead = ({ title, count }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
    <h2 className="syne" style={{ fontSize: 17, fontWeight: 800 }}>{title}</h2>
    {count !== undefined && (
      <span style={{ fontSize: 11, padding: '2px 9px', borderRadius: 100, background: 'rgba(254,52,232,0.15)', color: C.magenta, fontWeight: 700 }}>{count}</span>
    )}
  </div>
);

// ── Pill badge ──────────────────────────────────
const Pill = ({ label, color }) => (
  <span style={{ fontSize: 10, padding: '2px 9px', borderRadius: 100, background: `${color}18`, color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{label}</span>
);

// ── Action button ───────────────────────────────
const ActionBtn = ({ label, color, onClick, small }) => (
  <button
    onClick={onClick}
    style={{ padding: small ? '5px 12px' : '8px 16px', fontSize: small ? 11 : 12, fontWeight: 700, borderRadius: 7, border: 'none', background: `${color}18`, color, cursor: 'pointer', transition: 'all 0.2s', whiteSpace: 'nowrap' }}
    onMouseEnter={e => { e.currentTarget.style.background = `${color}30`; }}
    onMouseLeave={e => { e.currentTarget.style.background = `${color}18`; }}
  >
    {label}
  </button>
);

// ════════════════════════════════════════════════
// MAIN ADMIN PORTAL
// ════════════════════════════════════════════════
export default function AdminPortal() {
  const navigate = useNavigate();
  const { user, profile, loading: authLoading } = useAuth();
  const [tab,     setTab]     = useState('overview');
  const [loading, setLoading] = useState(true);

  // Data
  const [stats,       setStats]       = useState({ totalUsers: 0, talents: 0, collaborators: 0, pendingKYC: 0, liveJobs: 0, pendingJobs: 0 });
  const [users,       setUsers]       = useState([]);
  const [jobs,        setJobs]        = useState([]);
  const [kycList,     setKycList]     = useState([]);
  const [applications,setApplications]= useState([]);
  const [appFilter,   setAppFilter]   = useState('all');
  const [search,      setSearch]      = useState('');

  // Comment modal
  const [commentJob,  setCommentJob]  = useState(null); // job being commented on
  const [commentText, setCommentText] = useState('');
  const [commenting,  setCommenting]  = useState(false);

  // Guard: only admins
  useEffect(() => {
    if (!authLoading && (!user || profile?.role !== 'admin')) {
      navigate('/');
    }
  }, [user, profile, authLoading, navigate]);

  // Live-subscribe to jobs so moderation updates are instant
  useEffect(() => {
    if (!user || profile?.role !== 'admin') return;
    const q = query(collection(db, 'opportunities'), limit(200));
    const unsub = onSnapshot(q, snap => {
      const allJobs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      allJobs.sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
      setJobs(allJobs);
      setStats(s => ({
        ...s,
        liveJobs:    allJobs.filter(j => j.status === 'live').length,
        pendingJobs: allJobs.filter(j => j.status === 'pending' || !j.status).length,
      }));
    });
    return () => unsub();
  }, [user, profile]);

  // Live-subscribe to all applications
  useEffect(() => {
    if (!user || profile?.role !== 'admin') return;
    const q = query(collection(db, 'applications'), limit(500));
    const unsub = onSnapshot(q, snap => {
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      docs.sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
      setApplications(docs);
    });
    return () => unsub();
  }, [user, profile]);

  // Notification count derived directly from jobs — no separate collection needed
  const notifCount = jobs.filter(j => j.hasNewCollaboratorReply).length + jobs.filter(j => !j.status || j.status === 'pending').length;

  // Load all data from Firestore
  useEffect(() => {
    if (!user || profile?.role !== 'admin') return;
    loadAll();
  }, [user, profile]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [usersSnap, jobsSnap] = await Promise.all([
        getDocs(query(collection(db, 'users'), limit(200))),
        getDocs(query(collection(db, 'opportunities'), limit(200))),
      ]);

      const allUsers = usersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const allJobs  = jobsSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      setUsers(allUsers);
      setJobs(allJobs);
      setKycList(allUsers.filter(u => u.kycStatus === 'pending'));

      setStats({
        totalUsers:    allUsers.length,
        talents:       allUsers.filter(u => u.role === 'talent').length,
        collaborators: allUsers.filter(u => u.role === 'collaborator').length,
        pendingKYC:    allUsers.filter(u => u.kycStatus === 'pending').length,
        liveJobs:      allJobs.filter(j => j.status === 'live').length,
        pendingJobs:   allJobs.filter(j => j.status === 'pending' || !j.status).length,
      });
    } catch (e) {
      toast.error('Failed to load data: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  // ── User actions ───────────────────────────
  const suspendUser = async (userId, suspended) => {
    try {
      await updateDoc(doc(db, 'users', userId), { suspended: !suspended });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, suspended: !suspended } : u));
      toast.success(`User ${!suspended ? 'suspended' : 'reinstated'}`);
    } catch (e) { toast.error(e.message); }
  };

  const changeRole = async (userId, newRole) => {
    try {
      await updateDoc(doc(db, 'users', userId), { role: newRole });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
      toast.success(`Role updated to ${newRole}`);
    } catch (e) { toast.error(e.message); }
  };

  // ── KYC actions ────────────────────────────
  const reviewKYC = async (userId, decision) => {
    try {
      await updateDoc(doc(db, 'users', userId), { kycStatus: decision });
      setKycList(prev => prev.filter(u => u.id !== userId));
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, kycStatus: decision } : u));
      setStats(s => ({ ...s, pendingKYC: s.pendingKYC - 1 }));
      toast.success(`KYC ${decision} for user`);
    } catch (e) { toast.error(e.message); }
  };

  // ── Job moderation (uses service → notifies collaborator) ──────────
  const moderateJob = async (jobId, status) => {
    try {
      await moderateOpportunity(jobId, status, profile?.displayName || 'Admin');
      // jobs state updated by onSnapshot
      toast.success(`Job ${status === 'live' ? '✅ approved' : status === 'rejected' ? '❌ rejected' : `set to ${status}`} — collaborator notified`);
    } catch (e) { toast.error(e.message); }
  };

  const handleToggleFeatured = async (jobId, current) => {
    try {
      await toggleFeatured(jobId, !current);
      toast.success(!current ? 'Featured ⭐' : 'Unfeatured');
    } catch (e) { toast.error(e.message); }
  };

  const handleToggleUrgent = async (jobId, current) => {
    try {
      await toggleUrgent(jobId, !current);
      toast.success(!current ? '🔥 Marked Urgent' : 'Urgency removed');
    } catch (e) { toast.error(e.message); }
  };

  const handleAddComment = async () => {
    if (!commentText.trim() || !commentJob) return;
    setCommenting(true);
    try {
      await addAdminComment(commentJob.id, user.uid, profile?.displayName || 'Admin', commentText.trim());
      setCommentText('');
      toast.success('💬 Comment sent — collaborator notified');
      setCommentJob(null);
    } catch (e) { toast.error(e.message); }
    finally { setCommenting(false); }
  };

  // Clear the "new reply" flag when admin opens the thread
  const openCommentThread = async (job) => {
    setCommentJob(job);
    setCommentText('');
    if (job.hasNewCollaboratorReply) {
      try {
        await updateDoc(doc(db, 'opportunities', job.id), { hasNewCollaboratorReply: false });
      } catch (_) {}
    }
  };

  const filteredUsers = users.filter(u => {
    const q = search.toLowerCase();
    return !q || u.email?.toLowerCase().includes(q) || u.displayName?.toLowerCase().includes(q) || u.fullName?.toLowerCase().includes(q);
  });

  const roleColor = { admin: C.magenta, collaborator: C.info, talent: C.success };
  const kycColor  = { approved: C.success, pending: C.warning, rejected: C.danger };

  if (authLoading || loading) {
    return (
      <div style={{ paddingTop: 64, minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}>
        <Spin />
        <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 14 }}>Loading admin panel…</p>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  const TABS = [
    { id: 'overview',  label: '📊 Overview' },
    { id: 'users',        label: `👥 Users (${users.length})` },
    { id: 'jobs',         label: `💼 Jobs (${jobs.length})` },
    { id: 'applications', label: `📨 Applications (${applications.length})`, alert: applications.filter(a => a.status === 'new').length > 0 },
    { id: 'kyc',          label: `🪪 KYC (${kycList.length})`, alert: kycList.length > 0 },
  ];

  return (
    <div style={{ background: C.bg, paddingTop: 64, minHeight: '100vh' }}>
      <style>{`
        @keyframes spin{to{transform:rotate(360deg)}}
        .admin-stats-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px,1fr)); gap: 14px; margin-bottom: 28px; }
        .admin-user-table { overflow-x: auto; border-radius: 14px; border: 1px solid rgba(254,52,232,0.1); }
        .admin-user-row   { display: grid; grid-template-columns: 2fr 1.5fr 1fr 1fr 1fr auto; gap: 12px; padding: 14px 20px; border-bottom: 1px solid rgba(255,255,255,0.04); align-items: center; min-width: 700px; }
        .admin-user-head  { display: grid; grid-template-columns: 2fr 1.5fr 1fr 1fr 1fr auto; gap: 12px; padding: 12px 20px; border-bottom: 1px solid rgba(255,255,255,0.06); background: rgba(255,255,255,0.02); min-width: 700px; }
        @media (max-width: 768px) {
          .admin-tabs { gap: 6px !important; }
          .admin-tabs button { padding: 8px 14px !important; font-size: 12px !important; }
          .admin-stats-grid { grid-template-columns: 1fr 1fr !important; gap: 10px !important; }
        }
        @media (max-width: 480px) {
          .admin-stats-grid { grid-template-columns: 1fr 1fr !important; }
        }
      `}</style>

      {/* Header */}
      <div style={{ background: `linear-gradient(180deg,${C.navy}70,transparent)`, padding: '40px 0 32px', position: 'relative', borderBottom: '1px solid rgba(254,52,232,0.08)' }}>
        <div className="grid-bg" style={{ opacity: 0.3 }} />
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginBottom: 24 }}>
            <div>
              <span className="pill" style={{ background: 'rgba(254,52,232,0.1)', border: '1px solid rgba(254,52,232,0.25)', color: C.magenta, marginBottom: 12, display: 'inline-flex' }}>⚡ Admin Panel</span>
              <h1 className="syne" style={{ fontSize: 'clamp(1.6rem,3vw,2.4rem)', fontWeight: 800, letterSpacing: '-0.02em' }}>
                Oracle Nexus <span style={{ color: C.magenta }}>Control Centre</span>
              </h1>
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>
                Logged in as <strong style={{ color: C.magenta }}>{profile?.displayName || user?.email}</strong>
              </p>
            </div>
            <button onClick={loadAll} style={{ padding: '10px 20px', background: 'rgba(254,52,232,0.1)', border: '1px solid rgba(254,52,232,0.25)', borderRadius: 9, color: C.magenta, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              🔄 Refresh
            </button>
          </div>

          {/* Tab nav */}
          <div className="admin-tabs" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {TABS.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} style={{ position: 'relative', padding: '9px 20px', borderRadius: 9, background: tab === t.id ? `linear-gradient(135deg,${C.magenta},${C.violet})` : 'rgba(255,255,255,0.05)', border: tab === t.id ? 'none' : '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Syne',sans-serif", transition: 'all 0.2s', boxShadow: tab === t.id ? '0 6px 20px rgba(254,52,232,0.3)' : 'none' }}>
                {t.label}
                {t.alert && <div style={{ position: 'absolute', top: 6, right: 6, width: 7, height: 7, borderRadius: '50%', background: C.warning }} />}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="container" style={{ padding: '28px clamp(16px,4vw,28px) 60px' }}>

        {/* ── OVERVIEW ── */}
        {tab === 'overview' && (
          <div>
            <div className="admin-stats-grid">
              <StatCard icon="👥" label="Total Users"    value={stats.totalUsers}    color={C.magenta} sub={`${stats.talents} talents · ${stats.collaborators} companies`} />
              <StatCard icon="💼" label="Live Jobs"      value={stats.liveJobs}      color={C.success} sub={`${stats.pendingJobs} pending review`} />
              <StatCard icon="🪪" label="Pending KYC"    value={stats.pendingKYC}    color={C.warning} sub="Awaiting review" />
              <StatCard icon="🏢" label="Companies"      value={stats.collaborators} color={C.info} />
              <StatCard icon="🎯" label="Talents"        value={stats.talents}       color={C.violet} />
              <StatCard icon="📋" label="Total Jobs"     value={jobs.length}         color={C.lavender} />
              <StatCard icon="📨" label="Applications"   value={applications.length} color={C.info}    sub={`${applications.filter(a=>a.status==="new").length} new`} />
            </div>

            {/* Recent users */}
            <div className="card" style={{ padding: 24, marginBottom: 20 }}>
              <SectionHead title="Recent Signups" count={users.slice(0,5).length} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {users.slice(0,5).map((u, i) => (
                  <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 14px', borderRadius: 10, background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <div style={{ width: 38, height: 38, borderRadius: '50%', background: `linear-gradient(135deg,${C.magenta},${C.violet})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 800, flexShrink: 0 }}>
                      {(u.displayName || u.fullName || u.email)?.charAt(0)?.toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.displayName || u.fullName || 'Unknown'}</div>
                      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>{u.email}</div>
                    </div>
                    <Pill label={u.role || 'talent'} color={roleColor[u.role] || C.success} />
                    <Pill label={u.kycStatus || 'pending'} color={kycColor[u.kycStatus] || C.warning} />
                  </div>
                ))}
              </div>
            </div>

            {/* Pending jobs */}
            {jobs.filter(j => !j.status || j.status === 'pending').length > 0 && (
              <div className="card" style={{ padding: 24 }}>
                <SectionHead title="Jobs Awaiting Approval" count={jobs.filter(j => !j.status || j.status === 'pending').length} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {jobs.filter(j => !j.status || j.status === 'pending').slice(0,4).map((j, i) => (
                    <div key={j.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', borderRadius: 10, background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.06)' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="syne" style={{ fontSize: 14, fontWeight: 700 }}>{j.title}</div>
                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>{j.company} · {j.location}</div>
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <ActionBtn label="✅ Approve" color={C.success} onClick={() => moderateJob(j.id, 'live')} small />
                        <ActionBtn label="❌ Reject"  color={C.danger}  onClick={() => moderateJob(j.id, 'rejected')} small />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── USERS ── */}
        {tab === 'users' && (
          <div>
            <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
              <div style={{ position: 'relative', flex: 1, minWidth: 240 }}>
                <span style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.3)', pointerEvents: 'none' }}>🔍</span>
                <input
                  placeholder="Search by name or email…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  style={{ width: '100%', padding: '11px 14px 11px 38px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 9, color: '#fff', fontSize: 14, outline: 'none' }}
                  onFocus={e => e.target.style.borderColor = C.magenta}
                  onBlur={e  => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
                />
              </div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', padding: '11px 0' }}>{filteredUsers.length} users</div>
            </div>

            <div className="admin-user-table">
              <div className="admin-user-head">
                {['User', 'Email', 'Role', 'KYC', 'Status', 'Actions'].map(h => (
                  <div key={h} style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{h}</div>
                ))}
              </div>
              {filteredUsers.slice(0, 50).map((u, i) => (
                <div key={u.id} className="admin-user-row" style={{ background: u.suspended ? 'rgba(239,68,68,0.04)' : 'transparent', transition: 'background 0.2s' }}
                  onMouseEnter={e => { if (!u.suspended) e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = u.suspended ? 'rgba(239,68,68,0.04)' : 'transparent'; }}
                >
                  {/* Name */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: `linear-gradient(135deg,${C.magenta},${C.violet})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, flexShrink: 0 }}>
                      {(u.displayName || u.fullName || u.email)?.charAt(0)?.toUpperCase()}
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {u.displayName || u.fullName || 'Unknown'}
                    </div>
                  </div>
                  {/* Email */}
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</div>
                  {/* Role */}
                  <div>
                    <select
                      value={u.role || 'talent'}
                      onChange={e => changeRole(u.id, e.target.value)}
                      style={{ background: '#0f0f24', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, color: roleColor[u.role] || C.success, fontSize: 11, padding: '4px 8px', cursor: 'pointer', outline: 'none', fontWeight: 700 }}
                    >
                      {['talent', 'collaborator', 'admin'].map(r => <option key={r} style={{ background: '#0f0f24', color: '#fff' }}>{r}</option>)}
                    </select>
                  </div>
                  {/* KYC */}
                  <div><Pill label={u.kycStatus || 'pending'} color={kycColor[u.kycStatus] || C.warning} /></div>
                  {/* Status */}
                  <div><Pill label={u.suspended ? 'Suspended' : 'Active'} color={u.suspended ? C.danger : C.success} /></div>
                  {/* Actions */}
                  <ActionBtn
                    label={u.suspended ? 'Reinstate' : 'Suspend'}
                    color={u.suspended ? C.success : C.danger}
                    onClick={() => suspendUser(u.id, u.suspended)}
                    small
                  />
                </div>
              ))}
              {filteredUsers.length === 0 && (
                <div style={{ padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: 14 }}>No users found</div>
              )}
            </div>
          </div>
        )}

        {/* ── JOBS ── */}
        {tab === 'jobs' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {[
                  { label: 'All',       filter: null },
                  { label: '🕐 Pending', filter: 'pending' },
                  { label: '✅ Live',    filter: 'live' },
                  { label: '❌ Rejected',filter: 'rejected' },
                  { label: '⏸ Suspended',filter:'suspended' },
                ].map(f => {
                  const count = f.filter
                    ? jobs.filter(j => j.status === f.filter || (!j.status && f.filter === 'pending')).length
                    : jobs.length;
                  return (
                    <button key={f.label} style={{ padding: '7px 14px', borderRadius: 100, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                      {f.label} ({count})
                    </button>
                  );
                })}
              </div>
              {notifCount > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', borderRadius: 9, background: 'rgba(254,52,232,0.1)', border: '1px solid rgba(254,52,232,0.25)', fontSize: 12, color: C.magenta, fontWeight: 700 }}>
                  🔔 {notifCount} new notification{notifCount > 1 ? 's' : ''}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {jobs.map((j) => {
                const statusColor = { live: C.success, rejected: C.danger, suspended: C.warning, pending: C.warning }[j.status] || C.warning;
                const hasThread = j.comments?.length > 0;
                return (
                  <div key={j.id} className="card" style={{ padding: '22px 24px' }}>
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg,${statusColor}70,transparent)` }} />
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
                      {/* Left: info */}
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5, flexWrap: 'wrap' }}>
                          <span className="syne" style={{ fontSize: 15, fontWeight: 800 }}>{j.title}</span>
                          <Pill label={j.status || 'pending'} color={statusColor} />
                          {j.featured && <Pill label="⭐ Featured" color={C.magenta} />}
                          {j.urgent   && <Pill label="🔥 Urgent"   color={C.danger}  />}
                        </div>
                        <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.5)', marginBottom: 4 }}>
                          <strong style={{ color: 'rgba(255,255,255,0.75)' }}>{j.collaboratorName || 'Unknown Company'}</strong>
                          {j.company && j.company !== j.collaboratorName && ` · ${j.company}`}
                          {j.location && ` · ${j.location}`}
                          {j.type     && ` · ${j.type}`}
                        </div>
                        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', marginBottom: 8 }}>
                          {j.description?.slice(0, 140)}{j.description?.length > 140 ? '…' : ''}
                        </div>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                          {(j.skills || []).slice(0, 6).map(s => (
                            <span key={s} style={{ fontSize: 10, padding: '2px 8px', borderRadius: 5, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.5)' }}>{s}</span>
                          ))}
                        </div>
                        {/* Existing comment thread preview */}
                        {hasThread && (
                          <button onClick={() => openCommentThread(j)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8, background: 'rgba(254,52,232,0.08)', border: '1px solid rgba(254,52,232,0.2)', color: C.magenta, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                            💬 {j.comments.length} comment{j.comments.length !== 1 ? 's' : ''} · View thread
                          </button>
                        )}
                      </div>

                      {/* Right: moderation actions */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0, minWidth: 160 }}>
                        <div style={{ fontSize: 13, fontWeight: 800, color: C.success, fontFamily: "'Syne',sans-serif", textAlign: 'right' }}>
                          {j.payDisplay || (j.payMin && j.payMax ? `${j.payMin}–${j.payMax} ONT/mo` : 'Negotiable')}
                        </div>

                        {/* Status actions */}
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                          {j.status !== 'live'      && <ActionBtn label="✅ Approve"  color={C.success} onClick={() => moderateJob(j.id, 'live')}      small />}
                          {j.status !== 'rejected'  && <ActionBtn label="❌ Reject"   color={C.danger}  onClick={() => moderateJob(j.id, 'rejected')}  small />}
                          {j.status === 'live'      && <ActionBtn label="⏸ Suspend"  color={C.warning} onClick={() => moderateJob(j.id, 'suspended')} small />}
                          {j.status === 'suspended' && <ActionBtn label="▶ Reinstate" color={C.success} onClick={() => moderateJob(j.id, 'live')}      small />}
                        </div>

                        {/* Feature / Urgent toggles */}
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                          <button onClick={() => handleToggleFeatured(j.id, j.featured)} style={{ padding: '4px 10px', fontSize: 10, fontWeight: 700, borderRadius: 6, border: `1px solid ${j.featured ? C.magenta : 'rgba(255,255,255,0.1)'}`, background: j.featured ? 'rgba(254,52,232,0.1)' : 'transparent', color: j.featured ? C.magenta : 'rgba(255,255,255,0.5)', cursor: 'pointer' }}>
                            ⭐ {j.featured ? 'Unfeature' : 'Feature'}
                          </button>
                          <button onClick={() => handleToggleUrgent(j.id, j.urgent)} style={{ padding: '4px 10px', fontSize: 10, fontWeight: 700, borderRadius: 6, border: `1px solid ${j.urgent ? C.danger : 'rgba(255,255,255,0.1)'}`, background: j.urgent ? 'rgba(239,68,68,0.1)' : 'transparent', color: j.urgent ? C.danger : 'rgba(255,255,255,0.5)', cursor: 'pointer' }}>
                            🔥 {j.urgent ? 'Un-urgent' : 'Urgent'}
                          </button>
                        </div>

                        {/* Comment button */}
                        <button onClick={() => openCommentThread(j)} style={{ padding: '7px 12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: 'rgba(255,255,255,0.7)', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, justifyContent: 'center' }}>
                          💬 Comment
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
              {jobs.length === 0 && <div style={{ textAlign: 'center', padding: '60px 0', color: 'rgba(255,255,255,0.3)' }}>No jobs found</div>}
            </div>
          </div>
        )}

        {/* ── APPLICATIONS ── */}
        {tab === 'applications' && (() => {
          const STATUS_COLORS = { new: C.info, shortlisted: C.success, rejected: C.danger, hired: C.magenta };
          const STATUS_LABELS = { new: '🆕 New', shortlisted: '⭐ Shortlisted', rejected: '❌ Rejected', hired: '🎉 Hired' };
          const newCount = applications.filter(a => a.status === 'new').length;

          const filtered = appFilter === 'all'
            ? applications
            : applications.filter(a => a.status === appFilter);

          return (
            <div>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20, flexWrap:'wrap', gap:12 }}>
                <div>
                  <SectionHead title="All Applications" count={applications.length} />
                  {newCount > 0 && <div style={{ fontSize:12, color:C.info, marginTop:-8 }}>🔵 {newCount} new unreviewed</div>}
                </div>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  {['all','new','shortlisted','rejected','hired'].map(s => (
                    <button key={s} onClick={() => setAppFilter(s)} style={{ padding:'7px 13px', borderRadius:8, fontSize:11, fontWeight:700, cursor:'pointer', textTransform:'capitalize', background: appFilter===s ? `linear-gradient(135deg,${C.magenta},${C.violet})` : 'rgba(255,255,255,0.05)', border: appFilter===s ? 'none' : '1px solid rgba(255,255,255,0.1)', color:'#fff' }}>
                      {s==='all' ? `All (${applications.length})` : `${STATUS_LABELS[s]} (${applications.filter(a=>a.status===s).length})`}
                    </button>
                  ))}
                </div>
              </div>

              {applications.length === 0 && (
                <div style={{ textAlign:'center', padding:'60px 0', color:'rgba(255,255,255,0.3)' }}>
                  <div style={{ fontSize:44, marginBottom:12 }}>📨</div>
                  <p style={{ fontSize:15 }}>No applications yet across any listing.</p>
                </div>
              )}
              {applications.length > 0 && filtered.length === 0 && (
                <div style={{ textAlign:'center', padding:'40px 0', color:'rgba(255,255,255,0.3)' }}>No applications match this filter.</div>
              )}

              <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                {filtered.map(a => {
                  const statusColor = STATUS_COLORS[a.status] || C.info;
                  return (
                    <div key={a.id} className="card" style={{ padding:'20px 24px' }}>
                      <div style={{ position:'absolute', top:0, left:0, right:0, height:2, background:`linear-gradient(90deg,${statusColor}70,transparent)` }} />
                      <div style={{ display:'flex', alignItems:'flex-start', gap:14, flexWrap:'wrap' }}>
                        {/* Avatar */}
                        <div style={{ width:46, height:46, borderRadius:'50%', background:`linear-gradient(135deg,${C.magenta},${C.violet})`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, fontWeight:800, flexShrink:0, fontFamily:"'Syne',sans-serif" }}>
                          {(a.userName||a.userEmail)?.charAt(0)?.toUpperCase()}
                        </div>
                        {/* Info */}
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', marginBottom:3 }}>
                            <span className="syne" style={{ fontSize:15, fontWeight:800 }}>{a.userName}</span>
                            <Pill label={STATUS_LABELS[a.status]} color={statusColor} />
                          </div>
                          <div style={{ fontSize:12, color:'rgba(255,255,255,0.45)', marginBottom:4 }}>{a.userEmail}</div>
                          <div style={{ fontSize:12, color:'rgba(255,255,255,0.35)', marginBottom:6 }}>
                            Applied for: <strong style={{ color:'rgba(255,255,255,0.6)' }}>{a.opportunityTitle}</strong>
                            {' · '}<span style={{ color:'rgba(255,255,255,0.4)' }}>{a.company}</span>
                          </div>
                          <div style={{ fontSize:11, color:'rgba(255,255,255,0.25)' }}>
                            {a.createdAt?.toDate?.().toLocaleDateString() || 'Recently'}
                            {a.kycStatus && <span style={{ marginLeft:10, padding:'1px 7px', borderRadius:100, background: a.kycStatus==='approved' ? 'rgba(0,208,132,0.12)' : 'rgba(245,158,11,0.12)', color: a.kycStatus==='approved' ? C.success : C.warning, fontWeight:700 }}>🪪 KYC {a.kycStatus}</span>}
                          </div>
                        </div>
                        {/* Collaborator info */}
                        <div style={{ fontSize:11, color:'rgba(255,255,255,0.35)', textAlign:'right', flexShrink:0 }}>
                          <div>via {a.company || 'Unknown'}</div>
                          {a.cvUrl && <a href={a.cvUrl} target="_blank" rel="noreferrer" style={{ color:C.info, fontWeight:600, textDecoration:'none', marginTop:4, display:'block' }}>📄 CV</a>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* ── KYC ── */}
        {tab === 'kyc' && (
          <div>
            <SectionHead title="KYC Verification Queue" count={kycList.length} />
            {kycList.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 0' }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
                <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 15 }}>All clear! No pending KYC submissions.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {kycList.map((u, i) => (
                  <div key={u.id} className="card" style={{ padding: '22px 24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                      <div style={{ width: 52, height: 52, borderRadius: '50%', background: `linear-gradient(135deg,${C.magenta},${C.violet})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 800, flexShrink: 0 }}>
                        {(u.displayName || u.fullName || u.email)?.charAt(0)?.toUpperCase()}
                      </div>
                      <div style={{ flex: 1, minWidth: 180 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
                          <span className="syne" style={{ fontSize: 15, fontWeight: 700 }}>{u.displayName || u.fullName}</span>
                          <Pill label={u.role || 'talent'} color={roleColor[u.role] || C.success} />
                        </div>
                        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', marginBottom: 4 }}>{u.email}</div>
                        {u.phone && <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>📱 {u.phone}</div>}
                      </div>
                      <div style={{ display: 'flex', gap: 10, flexShrink: 0 }}>
                        <button
                          onClick={() => reviewKYC(u.id, 'approved')}
                          style={{ padding: '10px 20px', background: `linear-gradient(135deg,${C.success},#00b36b)`, border: 'none', borderRadius: 9, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Syne',sans-serif" }}
                        >
                          ✅ Approve
                        </button>
                        <button
                          onClick={() => reviewKYC(u.id, 'rejected')}
                          style={{ padding: '10px 20px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 9, color: C.danger, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                        >
                          ❌ Reject
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* All users with KYC status */}
            <div style={{ marginTop: 32 }}>
              <SectionHead title="All KYC Status" count={users.length} />
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr 1fr', gap: 12, padding: '12px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)' }}>
                  {['User', 'Email', 'KYC Status', 'Actions'].map(h => (
                    <div key={h} style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{h}</div>
                  ))}
                </div>
                {users.slice(0, 30).map(u => (
                  <div key={u.id} style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr 1fr', gap: 12, padding: '12px 20px', borderBottom: '1px solid rgba(255,255,255,0.04)', alignItems: 'center' }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{u.displayName || u.fullName || 'Unknown'}</div>
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>{u.email}</div>
                    <div><Pill label={u.kycStatus || 'pending'} color={kycColor[u.kycStatus] || C.warning} /></div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {u.kycStatus !== 'approved' && <ActionBtn label="Approve" color={C.success} onClick={() => reviewKYC(u.id, 'approved')} small />}
                      {u.kycStatus !== 'rejected' && <ActionBtn label="Reject"  color={C.danger}  onClick={() => reviewKYC(u.id, 'rejected')} small />}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

      </div>

      {/* ── Admin Comment Modal ─────────────────────────────────────── */}
      {commentJob && (
        <div onClick={e => e.target === e.currentTarget && setCommentJob(null)} style={{ position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(4,4,15,0.9)', backdropFilter: 'blur(14px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ width: '100%', maxWidth: 580, background: C.surface2, border: '1px solid rgba(254,52,232,0.25)', borderRadius: 20, overflow: 'hidden', display: 'flex', flexDirection: 'column', maxHeight: '88vh', animation: 'fadeUp .3s ease' }}>
            {/* Header */}
            <div style={{ padding: '18px 22px', borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0, background: 'rgba(254,52,232,0.04)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 4 }}>Admin Comment Thread</div>
                  <h3 className="syne" style={{ fontSize: 17, fontWeight: 800 }}>{commentJob.title}</h3>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>
                    By {commentJob.collaboratorName} · Status: <span style={{ color: { live: C.success, rejected: C.danger, suspended: C.warning, pending: C.warning }[commentJob.status] || C.warning, fontWeight: 700, textTransform: 'capitalize' }}>{commentJob.status || 'pending'}</span>
                  </div>
                </div>
                <button onClick={() => setCommentJob(null)} style={{ background: 'rgba(255,255,255,0.07)', border: 'none', color: 'white', width: 30, height: 30, borderRadius: 7, cursor: 'pointer', fontSize: 14, flexShrink: 0 }}>✕</button>
              </div>
            </div>

            {/* Existing thread */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 22px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              {(!commentJob.comments || commentJob.comments.length === 0) && (
                <div style={{ textAlign: 'center', padding: '30px 0', color: 'rgba(255,255,255,0.3)' }}>
                  <div style={{ fontSize: 28, marginBottom: 8 }}>💬</div>
                  <p style={{ fontSize: 13 }}>No comments yet. Your comment will notify the collaborator.</p>
                </div>
              )}
              {(commentJob.comments || []).map((c) => {
                const isAdmin = c.role === 'admin';
                return (
                  <div key={c.id} style={{ display: 'flex', gap: 10, flexDirection: isAdmin ? 'row-reverse' : 'row' }}>
                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: isAdmin ? `linear-gradient(135deg,${C.magenta},${C.violet})` : `linear-gradient(135deg,${C.info},${C.navy})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, flexShrink: 0 }}>
                      {c.authorName?.charAt(0) || '?'}
                    </div>
                    <div style={{ maxWidth: '78%' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, flexDirection: isAdmin ? 'row-reverse' : 'row' }}>
                        <span style={{ fontSize: 12, fontWeight: 700 }}>{c.authorName}</span>
                        <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 100, background: isAdmin ? 'rgba(254,52,232,0.15)' : 'rgba(59,130,246,0.15)', color: isAdmin ? C.magenta : C.info, fontWeight: 700 }}>
                          {isAdmin ? 'Admin' : 'Collaborator'}
                        </span>
                        <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)' }}>{new Date(c.createdAt).toLocaleDateString()}</span>
                      </div>
                      <div style={{ padding: '10px 14px', borderRadius: isAdmin ? '12px 4px 12px 12px' : '4px 12px 12px 12px', background: isAdmin ? 'rgba(254,52,232,0.08)' : 'rgba(59,130,246,0.08)', border: `1px solid ${isAdmin ? 'rgba(254,52,232,0.15)' : 'rgba(59,130,246,0.15)'}`, fontSize: 13.5, lineHeight: 1.65, color: 'rgba(255,255,255,0.85)' }}>
                        {c.text}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* New comment input */}
            <div style={{ padding: '14px 22px 22px', borderTop: '1px solid rgba(255,255,255,0.06)', flexShrink: 0 }}>
              <div style={{ marginBottom: 8 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Leave a comment for collaborator</label>
              </div>
              <textarea
                value={commentText}
                onChange={e => setCommentText(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleAddComment(); }}
                placeholder="e.g. Please update the job description to include salary range. Also the skills list is too broad — narrow it down. (Ctrl+Enter to send)"
                rows={3}
                style={{ width: '100%', padding: '12px 14px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(254,52,232,0.2)', borderRadius: 9, color: '#fff', fontSize: 13, outline: 'none', resize: 'none', fontFamily: "'DM Sans',sans-serif", marginBottom: 10, boxSizing: 'border-box' }}
              />
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  onClick={handleAddComment}
                  disabled={!commentText.trim() || commenting}
                  style={{ flex: 1, padding: '11px', background: commentText.trim() ? `linear-gradient(135deg,${C.magenta},${C.violet})` : 'rgba(255,255,255,0.07)', border: 'none', borderRadius: 9, color: '#fff', fontWeight: 700, fontSize: 13, cursor: commentText.trim() ? 'pointer' : 'default', fontFamily: "'Syne',sans-serif", opacity: commenting ? 0.7 : 1 }}
                >
                  {commenting ? 'Sending…' : '💬 Send Comment to Collaborator'}
                </button>
                <button onClick={() => setCommentJob(null)} style={{ padding: '11px 18px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 9, color: '#fff', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
